import { ApiRequestError } from "./client.ts";
import { tokenManager } from "../utils/tokenManager";
import { userTimezoneHeaders } from "../utils/userTimezoneHeader";
import type {
  RecordingSchedulePutBody,
  RecordingScheduleResponse,
  RecordingScheduleSlots,
} from "../types/recordingSchedule.ts";

import { getNodeApiBaseUrl } from "./nodeApiBaseUrl.ts";

export { getNodeApiBaseUrl };

function buildUrl(path: string): string {
  const base = getNodeApiBaseUrl();
  let p = path.startsWith("/") ? path : `/${path}`;
  /** Avoid `http://host/api` + `/api/cameras` → double `/api`. */
  if (/\/api$/i.test(base) && /^\/api\//i.test(p)) {
    p = p.slice(4);
    if (!p.startsWith("/")) p = `/${p}`;
  }
  return `${base}${p}`;
}

function unwrapEntity(body: unknown): Record<string, unknown> | null {
  if (!body || typeof body !== "object") return null;
  const o = body as Record<string, unknown>;
  const inner = o.data;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    return inner as Record<string, unknown>;
  }
  return o;
}

function unwrapList(body: unknown): Record<string, unknown>[] {
  if (Array.isArray(body)) {
    return body.filter(
      (r): r is Record<string, unknown> => r != null && typeof r === "object",
    );
  }
  if (body && typeof body === "object") {
    const o = body as Record<string, unknown>;
    const d = o.data ?? o.results ?? o.Data;
    if (Array.isArray(d)) {
      return d.filter(
        (r): r is Record<string, unknown> => r != null && typeof r === "object",
      );
    }
  }
  return [];
}

/** Normalize Node list/detail row for administration table + dialog. */
export function adminRowFromServerCamera(
  raw: Record<string, unknown>,
): Record<string, unknown> {
  const id = String(raw.id ?? raw.camera_id ?? "").trim();
  const createdAt = raw.created_at ?? raw.createdAt;
  return {
    ...raw,
    id,
    camera_id: id,
    name: raw.name,
    type: raw.type ?? raw.camera_type ?? raw.cameraType,
    vendor: raw.vendor ?? raw.camera_vendor ?? raw.cameraVendor,
    rtmp_url: raw.rtmp_url ?? raw.rtmpUrl,
    cam360_rtsp:
      raw.cam360_rtsp ?? raw.cam360Rtsp ?? raw.streaming_url ?? raw.stream_url,
    streaming_url:
      raw.streaming_url ?? raw.cam360Rtsp ?? raw.cam360_rtsp ?? raw.stream_url,
    ptz_rtsp: raw.ptz_rtsp ?? raw.ptzRtsp,
    onvif_enabled: raw.onvif_enabled ?? raw.onvifEnabled,
    onvif_host: raw.onvif_host ?? raw.onvifHost,
    onvif_port: raw.onvif_port ?? raw.onvifPort,
    onvif_username: raw.onvif_username ?? raw.onvifUsername,
    recording_type: raw.recording_type ?? raw.recordingType,
    recording_enabled: raw.recording_enabled ?? raw.recordingEnabled,
    is_active: raw.is_active ?? raw.isActive,
    zoom_min: raw.zoom_min ?? raw.zoomMin,
    zoom_max: raw.zoom_max ?? raw.zoomMax,
    pan_offset_deg: raw.pan_offset_deg ?? raw.panOffsetDeg,
    tilt_offset_deg: raw.tilt_offset_deg ?? raw.tiltOffsetDeg,
    latitude: raw.latitude ?? raw.lat,
    longitude: raw.longitude ?? raw.lng ?? raw.lon,
    recording_schedule_slots:
      raw.recording_schedule_slots ?? raw.recordingScheduleSlots,
    recording_schedule_timezone:
      raw.recording_schedule_timezone ?? raw.recordingScheduleTimezone,
    created_at: createdAt,
  };
}

function parseJsonError(data: unknown): { message: string; code: string } {
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    const nested =
      o.error && typeof o.error === "object" && !Array.isArray(o.error)
        ? (o.error as Record<string, unknown>)
        : null;
    const message =
      (typeof o.message === "string" && o.message) ||
      (nested && typeof nested.message === "string" && nested.message) ||
      (typeof o.detail === "string" && o.detail) ||
      (typeof o.error === "string" && o.error) ||
      "Request failed";
    const code =
      (typeof o.code === "string" && o.code) ||
      (nested && typeof nested.code === "string" && nested.code) ||
      (typeof o.error === "string" && o.error) ||
      "REQUEST_FAILED";
    return { message, code };
  }
  return { message: "Request failed", code: "REQUEST_FAILED" };
}

async function nodeApiRequest(
  path: string,
  method: "GET" | "POST" | "PUT" | "DELETE",
  body?: unknown,
): Promise<unknown> {
  const url = buildUrl(path);
  const token = tokenManager.getToken();
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(method !== "GET" ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...userTimezoneHeaders(),
  };
  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const { message, code } = parseJsonError(data);
    if (
      data &&
      typeof data === "object" &&
      (data as { loggedIn?: boolean }).loggedIn === false
    ) {
      tokenManager.clearAuth();
      window.location.href = "/login";
    }
    throw new ApiRequestError(message, code, res.status);
  }
  return data;
}

async function nodeApiJson(
  path: string,
  method: "POST" | "PUT",
  body: unknown,
): Promise<unknown> {
  return nodeApiRequest(path, method, body);
}

function unwrapScheduleResponse(body: unknown): RecordingScheduleResponse {
  const row = unwrapEntity(body) ?? (body as Record<string, unknown>);
  const timezone = String(row.timezone ?? "").trim();
  const slotsRaw = row.slots;
  const slots =
    slotsRaw && typeof slotsRaw === "object" && !Array.isArray(slotsRaw)
      ? (slotsRaw as RecordingScheduleSlots)
      : null;
  const recordingType = row.recording_type ?? row.recordingType;
  return {
    timezone,
    slots,
    recording_type:
      recordingType === "scheduled" || recordingType === "continuous"
        ? recordingType
        : null,
    recording_enabled: Boolean(row.recording_enabled ?? row.recordingEnabled),
    is_active: Boolean(row.is_active ?? row.isActive),
    inside_schedule_now: Boolean(
      row.inside_schedule_now ?? row.insideScheduleNow,
    ),
    recording_active_now: Boolean(
      row.recording_active_now ?? row.recordingActiveNow,
    ),
    evaluated_day: (row.evaluated_day ?? row.evaluatedDay) as
      | RecordingScheduleResponse["evaluated_day"]
      | undefined,
    evaluated_slot:
      typeof row.evaluated_slot === "number"
        ? row.evaluated_slot
        : typeof row.evaluatedSlot === "number"
          ? row.evaluatedSlot
          : null,
    current_slot_selected:
      typeof row.current_slot_selected === "boolean"
        ? row.current_slot_selected
        : typeof row.currentSlotSelected === "boolean"
          ? row.currentSlotSelected
          : null,
  };
}

/** True when the camera row is an external (non-ONVIF) camera. */
export function adminCameraIsExternal(row: Record<string, unknown>): boolean {
  const source = String(row.camera_source ?? row.cameraSource ?? "")
    .trim()
    .toLowerCase();
  if (source === "external") return true;
  if (source === "internal") return false;
  return row.has_internal === false || row.hasInternal === false;
}

/** GET `/api/cameras` — list cameras from the Node API (`type=all` = internal + external). */
export async function adminNodeListCameras(
  search?: string,
  signal?: AbortSignal,
): Promise<Record<string, unknown>[]> {
  const params = new URLSearchParams({ type: "all" });
  if (search != null && search.trim() !== "") {
    params.set("search", search.trim());
  }
  const url = buildUrl(`/api/cameras?${params.toString()}`);
  const token = tokenManager.getToken();
  const res = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...userTimezoneHeaders(),
    },
    signal,
  });
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const { message, code } = parseJsonError(data);
    if (
      data &&
      typeof data === "object" &&
      (data as { loggedIn?: boolean }).loggedIn === false
    ) {
      tokenManager.clearAuth();
      window.location.href = "/login";
    }
    throw new ApiRequestError(message, code, res.status);
  }
  return unwrapList(data)
    .map(adminRowFromServerCamera)
    .filter((r) => String(r.id ?? "").trim() !== "");
}

export type AdminNodeRtmpUrlResponse = {
  uniqueId: string;
  rtmpUrl: string;
};

/** POST `/api/cameras/rtmp` — allocate a unique RTMP ingest URL for a new camera. */
export async function adminNodeGenerateRtmpUrl(): Promise<AdminNodeRtmpUrlResponse> {
  const data = await nodeApiRequest("/api/cameras/rtmp", "GET");
  const row = unwrapEntity(data) ?? (data as Record<string, unknown>);
  const uniqueId = String(row.uniqueId ?? row.unique_id ?? "").trim();
  const rtmpUrl = String(row.rtmpUrl ?? row.rtmp_url ?? "").trim();
  if (!rtmpUrl) {
    throw new ApiRequestError(
      "RTMP URL missing from server response",
      "INVALID_RESPONSE",
      502,
    );
  }
  return { uniqueId, rtmpUrl };
}

/** POST `/api/cameras` — create camera on the Node API. */
export async function adminNodeCreateCamera(
  body: Record<string, unknown>,
): Promise<Record<string, unknown> | null> {
  const data = await nodeApiJson("/api/cameras", "POST", body);
  return unwrapEntity(data);
}

/** PUT `/api/cameras/:id` — update camera on the Node API. */
export async function adminNodeUpdateCamera(
  id: string,
  body: Record<string, unknown>,
): Promise<Record<string, unknown> | null> {
  const encoded = encodeURIComponent(id);
  const data = await nodeApiJson(`/api/cameras/${encoded}`, "PUT", body);
  return unwrapEntity(data);
}

/** DELETE `/api/cameras/:id` — remove camera on the Node API (204 No Content). */
export async function adminNodeDeleteCamera(id: string): Promise<void> {
  const encoded = encodeURIComponent(id);
  await nodeApiRequest(`/api/cameras/${encoded}`, "DELETE");
}

/** GET `/api/cameras/:id/recording-schedule` */
export async function adminNodeGetRecordingSchedule(
  cameraId: string,
  signal?: AbortSignal,
): Promise<RecordingScheduleResponse> {
  const encoded = encodeURIComponent(cameraId);
  const url = buildUrl(`/api/cameras/${encoded}/recording-schedule`);
  const token = tokenManager.getToken();
  const res = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...userTimezoneHeaders(),
    },
    signal,
  });
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const { message, code } = parseJsonError(data);
    throw new ApiRequestError(message, code, res.status);
  }
  return unwrapScheduleResponse(data);
}

/** PUT `/api/cameras/:id/recording-schedule` */
export async function adminNodePutRecordingSchedule(
  cameraId: string,
  body: RecordingSchedulePutBody,
): Promise<RecordingScheduleResponse> {
  const encoded = encodeURIComponent(cameraId);
  const data = await nodeApiJson(
    `/api/cameras/${encoded}/recording-schedule`,
    "PUT",
    body,
  );
  return unwrapScheduleResponse(data);
}

export function resolveAdminCameraRowId(
  server: Record<string, unknown> | null,
  fallback: string,
): string {
  if (!server) return fallback;
  const id = server.id ?? server.camera_id;
  const s = id != null ? String(id).trim() : "";
  return s || fallback;
}

export function resolveAdminCameraCreatedAt(
  server: Record<string, unknown> | null,
  fallback: string,
): string {
  if (!server) return fallback;
  const raw =
    server.created_at ??
    server.createdAt ??
    server.updated_at ??
    server.updatedAt;
  const s = raw != null ? String(raw).trim() : "";
  return s || fallback;
}
